import { AlertCircle, Pencil } from "lucide-react";
import { useId, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { WALLET_DEBIT_EXTERNAL_REFERENCE_MAX } from "../constants/walletDebits.constants";
import { WalletDebitsService } from "../services/walletDebits.service";
import type { WalletDebit } from "../types/walletDebits.types";

/** A sent debit's reference, correctable: the timeline keeps the one it replaces. */
export function WalletDebitReferenceEditor({
  debit,
  canEdit,
  onChange,
}: {
  debit: WalletDebit;
  canEdit: boolean;
  onChange: (debit: WalletDebit) => void;
}) {
  const { t } = useTranslations("walletDebits");
  const inputId = useId();
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = draft?.trim() ?? "";

  const save = async () => {
    if (!trimmed || saving) return;
    setSaving(true);
    setError(null);
    try {
      onChange(await WalletDebitsService.updateReference(debit.id, trimmed));
      setDraft(null);
    } catch (saveError: unknown) {
      setError(HttpApiService.getErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  if (draft === null) {
    return (
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{t("done_external_reference")}</p>
          <p className="mt-0.5 font-mono text-[13px] font-medium break-all text-foreground">
            {debit.external_reference ?? "—"}
          </p>
        </div>
        {canEdit && (
          <ColocaButton
            size="sm"
            variant="ghost"
            icon={Pencil}
            onClick={() => setDraft(debit.external_reference ?? "")}
          >
            {t("reference_edit")}
          </ColocaButton>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label htmlFor={inputId} className="text-xs text-muted-foreground">
        {t("done_external_reference")}
      </label>
      <ColocaInput
        id={inputId}
        value={draft}
        onValueChange={setDraft}
        maxLength={WALLET_DEBIT_EXTERNAL_REFERENCE_MAX}
        autoComplete="off"
        disabled={saving}
        autoFocus
      />
      <p className="text-xs text-muted-foreground">{t("reference_edit_hint")}</p>
      {error && <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />}
      <div className="flex gap-2">
        <ColocaButton
          size="sm"
          variant="primary"
          loading={saving}
          disabled={!trimmed || trimmed === debit.external_reference || saving}
          onClick={() => void save()}
        >
          {t("reference_save")}
        </ColocaButton>
        <ColocaButton size="sm" variant="ghost" disabled={saving} onClick={() => setDraft(null)}>
          {t("action_cancel")}
        </ColocaButton>
      </div>
    </div>
  );
}
